
/**
 * Normaliza um embed do PostgREST para array.
 * Com a UNIQUE em analise_cadastral.negociacao_id (jul/2026), o PostgREST passou a
 * detectar a relação como 1→1 e devolve o embed como objeto; antes vinha como array.
 * O app inteiro lê `analise_cadastral?.[0]` — este helper aceita os dois formatos.
 */
export function embedToArray<T>(embed: T | T[] | null | undefined): T[] {
    if (embed == null) return [];
    return Array.isArray(embed) ? embed : [embed];
}

export function normalizeCpfCnpj(value: string | null | undefined): string {
    if (!value) return '—';
    const str = String(value).trim();
    if (!str) return '—';
    // Detect scientific notation stored by Excel with Brazilian locale (e.g. "2,70382E+13")
    if (/^\d[\d,]*[Ee][+\-]?\d+$/.test(str)) {
        const num = parseFloat(str.replace(',', '.'));
        if (!isNaN(num) && isFinite(num)) {
            return Math.round(num).toString();
        }
    }
    return str;
}

export function formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';

    // Check if it matches YYYY-MM-DD format (exactly 10 chars)
    // This is common for Postgres 'date' types which are timezone agnostic but often parsed as UTC midnight
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
    }

    // Fallback for full ISO strings or other formats (e.g. timestamps)
    // We use local time here as these usually represent a specific point in time
    try {
        return new Date(dateString).toLocaleDateString('pt-BR');
    } catch (e) {
        return dateString; // Return original if parsing fails
    }
}

export function parseLocalDate(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
}

/**
 * Remove apóstrofos, aspas e caracteres inválidos em paths do Windows/SharePoint
 * de um nome de cliente, para uso seguro em nomes de pasta/arquivo enviados a
 * integrações externas (ex: webhooks n8n que criam pastas no SharePoint).
 * O nome original (com os caracteres) deve continuar sendo salvo/exibido normalmente.
 */
export function sanitizeNomeParaArquivo(nome: string): string {
    if (!nome) return '';
    return nome
        .replace(/['’‘"“”`/\\:*?<>|]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

export function validarCPF(cpf: string): boolean {
    cpf = cpf.replace(/[^\d]+/g, '');
    if (cpf.length !== 11 || !!cpf.match(/(\d)\1{10}/)) return false;
    let split = cpf.split(''),
        v1 = 0,
        v2 = 0,
        aux = false;
    for (let i = 1; cpf.length > i; i++) {
        if (split[i - 1] != split[i]) {
            aux = true;
        }
    }
    if (aux == false) {
        return false;
    }
    for (let i = 0, p = 10; cpf.length - 2 > i; i++, p--) {
        v1 += parseInt(split[i]) * p;
    }
    v1 = (v1 * 10) % 11;
    if (v1 == 10 || v1 == 11) {
        v1 = 0;
    }
    if (v1 != parseInt(split[9])) {
        return false;
    }
    for (let i = 0, p = 11; cpf.length - 1 > i; i++, p--) {
        v2 += parseInt(split[i]) * p;
    }
    v2 = (v2 * 10) % 11;
    if (v2 == 10 || v2 == 11) {
        v2 = 0;
    }
    if (v2 != parseInt(split[10])) {
        return false;
    } else {
        return true;
    }
}

export function validarCNPJ(cnpj: string): boolean {
    cnpj = cnpj.replace(/[^\d]+/g, '');
    if (cnpj == '') return false;
    if (cnpj.length != 14) return false;

    // Elimina CNPJs invalidos conhecidos
    if (
        cnpj == '00000000000000' ||
        cnpj == '11111111111111' ||
        cnpj == '22222222222222' ||
        cnpj == '33333333333333' ||
        cnpj == '44444444444444' ||
        cnpj == '55555555555555' ||
        cnpj == '66666666666666' ||
        cnpj == '77777777777777' ||
        cnpj == '88888888888888' ||
        cnpj == '99999999999999'
    )
        return false;
    // Valida DVs
    let tamanho = cnpj.length - 2;
    let numeros = cnpj.substring(0, tamanho);
    const digitos = cnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
        soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
        if (pos < 2) pos = 9;
    }
    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado != parseInt(digitos.charAt(0))) return false;
    tamanho = tamanho + 1;
    numeros = cnpj.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
        soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
        if (pos < 2) pos = 9;
    }
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado != parseInt(digitos.charAt(1))) return false;
    return true;
}
